import { useEffect, useState, type ReactNode } from 'react';
import { cn } from '../utils/cn.js';

export interface AnnouncementBarProps {
  /** Stable id used for localStorage dismiss tracking. Bump when the message changes. */
  id: string;
  /** The announcement message. Kept to one sentence. */
  message: ReactNode;
  /** Where the CTA link goes. */
  href?: string;
  /** CTA link label. Defaults to "Learn more". */
  linkLabel?: string;
  /** Set to false to make the bar undismissible. */
  dismissible?: boolean;
  /** Visual tone. */
  tone?: 'info' | 'accent' | 'default';
  className?: string;
}

const STORAGE_PREFIX = 'kollez-announce:';

/**
 * `<AnnouncementBar>` — persistent, dismissible, single-line strip above
 * the header. Placement: slot inside PublicShell above the `<pub-h>` header.
 *
 * Dismissal is tracked per-`id` in localStorage; bump `id` (e.g. from
 * `season-2026` to `season-2027`) when the message changes so previously-
 * dismissed users see the new one.
 *
 * Motion: 220ms slide-down on mount if not previously dismissed. On
 * dismiss, 200ms slide-up then unmount. Respects `prefers-reduced-motion`
 * via the site-wide reduced-motion CSS block.
 *
 * Never render more than one AnnouncementBar at a time.
 */
export function AnnouncementBar({
  id,
  message,
  href,
  linkLabel = 'Learn more',
  dismissible = true,
  tone = 'accent',
  className,
}: AnnouncementBarProps) {
  const [visible, setVisible] = useState(false);
  const [entered, setEntered] = useState(false);

  // On mount, check dismissal state before painting to avoid flicker.
  useEffect(() => {
    try {
      const dismissed = window.localStorage.getItem(STORAGE_PREFIX + id) === '1';
      if (!dismissed) {
        setVisible(true);
        // Trigger slide-down transition on the next tick.
        requestAnimationFrame(() => setEntered(true));
      }
    } catch {
      // Private mode / storage disabled — show without persistence.
      setVisible(true);
      requestAnimationFrame(() => setEntered(true));
    }
  }, [id]);

  function dismiss() {
    setEntered(false);
    try {
      window.localStorage.setItem(STORAGE_PREFIX + id, '1');
    } catch {
      // ignore
    }
    // Unmount after slide-up transition.
    window.setTimeout(() => setVisible(false), 220);
  }

  if (!visible) return null;

  const toneClass =
    tone === 'accent'
      ? 'bg-info text-ink-accent'
      : tone === 'info'
        ? 'bg-info text-ink-primary'
        : 'bg-secondary text-ink-primary';

  return (
    <div
      role="region"
      aria-label="Site announcement"
      className={cn(
        'w-full border-b border-line-tertiary overflow-hidden',
        'transition-[max-height,opacity] duration-[220ms] ease-[cubic-bezier(0.16,1,0.3,1)]',
        entered ? 'max-h-16 opacity-100' : 'max-h-0 opacity-0',
        toneClass,
        className,
      )}
      style={{ zIndex: 'var(--z-header, 40)' }}
    >
      <div className="mx-auto max-w-page-wide px-5 lg:px-10 flex items-center gap-3 min-h-10 py-2 text-sm">
        <span className="flex-1">
          {message}
          {href && (
            <>
              {' '}
              <a href={href} className="link font-medium">
                {linkLabel} →
              </a>
            </>
          )}
        </span>
        {dismissible && (
          <button
            type="button"
            onClick={dismiss}
            aria-label="Dismiss announcement"
            className="p-1 -mr-1 rounded hover:bg-primary/20 focus-visible:outline-2 focus-visible:outline-offset-2"
            style={{ width: 32, height: 32 }}
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
              <path d="M1 1L13 13M13 1L1 13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </button>
        )}
      </div>
    </div>
  );
}
